import { MAX_QUERY_LIMIT, type Sort } from '@aibi/contracts';
import { DiagnosticBag, type AnalysisDiagnostic } from './diagnostics.js';
import { NON_COMPARABLE_TYPES } from './field-types.js';
import type { PlannedDimension, PlannedPagination, PlannedSort } from './query-plan.js';

/** Maximo de filas por pagina admitido por el contrato `QueryRequest`. */
export const MAX_PAGE_SIZE = 100_000;

export interface PlanPaginationOptions {
  readonly offset?: number;
  readonly pageSize?: number;
  /** `maxRows` de la ejecucion: una pagina no puede superarlo. */
  readonly maxRows?: number;
  /** Paginacion por cursor: el contrato actual no la admite (se reporta `unsupported`). */
  readonly cursor?: string;
}

export interface PaginationResolution {
  readonly pagination: PlannedPagination;
  /** `true` si el orden debe ser estable (hay limite o paginacion). */
  readonly requiresStableOrder: boolean;
  readonly diagnostics: readonly AnalysisDiagnostic[];
}

export interface SortingResolution {
  readonly sorting: readonly PlannedSort[];
  readonly diagnostics: readonly AnalysisDiagnostic[];
}

const isPositiveInteger = (value: number, max: number): boolean =>
  Number.isInteger(value) && value >= 1 && value <= max;

/**
 * Normaliza limite y paginacion por offset. Rechaza offsets fuera del limite, paginas
 * mayores que `maxRows` y cursores (no soportados por el contrato).
 *
 * @example
 * planPagination(100, { offset: 50, pageSize: 25 }).pagination; // { limit: 100, offset: 50, pageSize: 25 }
 */
export function planPagination(
  limit: number | null,
  options?: PlanPaginationOptions,
): PaginationResolution {
  const bag = new DiagnosticBag();
  if (limit !== null && !isPositiveInteger(limit, MAX_QUERY_LIMIT)) {
    bag.error('invalid-pagination', `El limite debe ser un entero entre 1 y ${MAX_QUERY_LIMIT}.`, {
      path: 'limit',
    });
  }
  if (!options) {
    return {
      pagination: { limit, offset: 0, pageSize: null },
      requiresStableOrder: limit !== null,
      diagnostics: bag.items,
    };
  }

  const offset = options.offset ?? 0;
  const pageSize = options.pageSize ?? null;
  if (options.cursor !== undefined) {
    bag.error('unsupported', 'El contrato de consulta solo admite paginacion por offset.', {
      path: 'pagination.cursor',
    });
  }
  if (!Number.isInteger(offset) || offset < 0) {
    bag.error('invalid-pagination', 'El offset debe ser un entero no negativo.', {
      path: 'pagination.offset',
    });
  }
  if (pageSize !== null && !isPositiveInteger(pageSize, MAX_PAGE_SIZE)) {
    bag.error(
      'invalid-pagination',
      `El tamano de pagina debe ser un entero entre 1 y ${MAX_PAGE_SIZE}.`,
      { path: 'pagination.pageSize' },
    );
  }
  if (pageSize !== null && options.maxRows !== undefined && pageSize > options.maxRows) {
    bag.error(
      'invalid-pagination',
      `El tamano de pagina (${pageSize}) supera el maximo de filas de la ejecucion (${options.maxRows}).`,
      { path: 'pagination.pageSize' },
    );
  }
  if (limit !== null && offset > 0 && offset >= limit) {
    bag.error(
      'invalid-pagination',
      `El offset (${offset}) debe ser menor que el limite del analisis (${limit}).`,
      { path: 'pagination.offset' },
    );
  }
  return {
    pagination: { limit, offset, pageSize },
    requiresStableOrder: true,
    diagnostics: bag.items,
  };
}

/**
 * Resuelve el orden: valida claves y tipos ordenables y, si se requiere orden estable,
 * anade como desempate las dimensiones no cubiertas (en su orden, ascendente).
 *
 * @example
 * planSorting([{ key: 'sales', direction: 'desc' }], dimensions, new Set(['sales']), true);
 */
export function planSorting(
  sorting: readonly Sort[],
  dimensions: readonly PlannedDimension[],
  measureKeys: ReadonlySet<string>,
  requiresStableOrder: boolean,
): SortingResolution {
  const bag = new DiagnosticBag();
  const dimensionsByKey = new Map(dimensions.map((dimension) => [dimension.key, dimension]));
  const planned: PlannedSort[] = [];
  const covered = new Set<string>();

  sorting.forEach((sort, i) => {
    const path = `sorting.${i}.key`;
    if (covered.has(sort.key)) {
      bag.warning('ignored', `Criterio de orden repetido "${sort.key}".`, { path });
      return;
    }
    const dimension = dimensionsByKey.get(sort.key);
    if (dimension) {
      if (dimension.timeGrain === null && NON_COMPARABLE_TYPES.has(dimension.dataType)) {
        bag.error('invalid-type', `No se puede ordenar por un campo ${dimension.dataType}.`, {
          path,
        });
        return;
      }
    } else if (!measureKeys.has(sort.key)) {
      bag.error('missing-reference', `Clave inexistente "${sort.key}".`, { path });
      return;
    }
    covered.add(sort.key);
    planned.push({
      key: sort.key,
      direction: sort.direction,
      target: dimension ? 'dimension' : 'measure',
      origin: 'state',
    });
  });

  if (requiresStableOrder) {
    const added: string[] = [];
    for (const dimension of dimensions) {
      if (covered.has(dimension.key) || NON_COMPARABLE_TYPES.has(dimension.dataType)) continue;
      covered.add(dimension.key);
      added.push(dimension.key);
      planned.push({ key: dimension.key, direction: 'asc', target: 'dimension', origin: 'stable' });
    }
    if (added.length > 0) {
      bag.info(
        'stable-order',
        `Se anade orden ascendente por ${added.join(', ')} para que la paginacion sea estable.`,
        { path: 'sorting' },
      );
    }
  }

  return { sorting: planned, diagnostics: bag.items };
}
